import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart as LineChartIcon, 
  MapPin, 
  Building2, 
  Calendar, 
  Thermometer,
  Droplets,
  Wind,
  Gauge,
  Cloud,
  Sun,
  X,
  TrendingUp,
  TrendingDown,
  Eye,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Check,
  ChevronDown,
  Filter,
  Waves,
  Download,
  FileText,
  FileSpreadsheet
} from "lucide-react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  Area,
  ComposedChart,
  BarChart,
  Bar,
  Cell
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import PremiumModal from "@/components/PremiumModal";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeRanges = [
  { value: "30", label: "30 Days" },
  { value: "90", label: "90 Days" },
  { value: "180", label: "6 Months" },
  { value: "365", label: "1 Year" },
];

const weatherMetrics = [
  { value: "temperature_mean", label: "Temp (Avg)", icon: Thermometer, color: "#F59E0B", category: "temperature" },
  { value: "temperature_max", label: "Temp (Max)", icon: Thermometer, color: "#EF4444", category: "temperature" },
  { value: "temperature_min", label: "Temp (Min)", icon: Thermometer, color: "#F97316", category: "temperature" },
  { value: "precipitation_sum", label: "Precipitation", icon: Droplets, color: "#38BDF8", category: "precipitation" },
  { value: "wind_speed_max", label: "Wind Speed", icon: Wind, color: "#A78BFA", category: "wind_speed" },
  { value: "pressure_msl_mean", label: "Pressure", icon: Gauge, color: "#6366F1", category: "pressure" },
  { value: "humidity_mean", label: "Humidity", icon: Droplets, color: "#06B6D4", category: "humidity" },
  { value: "cloud_cover_mean", label: "Cloud Cover", icon: Cloud, color: "#94A3B8", category: "cloud_cover" },
  { value: "uv_index_max", label: "UV Index", icon: Sun, color: "#FBBF24", category: "uv_index" },
  { value: "wave_height_max", label: "Wave Height", icon: Waves, color: "#0EA5E9", category: "tide" },
  { value: "wave_period_max", label: "Wave Period", icon: Waves, color: "#0284C7", category: "tide" },
  { value: "swell_wave_height_max", label: "Swell Height", icon: Waves, color: "#0369A1", category: "tide" },
];

const metricCategories = {
  temperature: { name: "Temperature", metrics: ["temperature_mean", "temperature_max", "temperature_min"] },
  tide: { name: "Tide", metrics: ["wave_height_max", "wave_period_max", "swell_wave_height_max"] },
  precipitation: { name: "Precipitation", metrics: ["precipitation_sum"] },
  wind_speed: { name: "Wind Speed", metrics: ["wind_speed_max"] },
  pressure: { name: "Pressure", metrics: ["pressure_msl_mean"] },
  humidity: { name: "Humidity", metrics: ["humidity_mean"] },
  cloud_cover: { name: "Cloud Cover", metrics: ["cloud_cover_mean"] },
  uv_index: { name: "UV Index", metrics: ["uv_index_max"] },
};

const getMetricInfo = (metricValue) => {
  return weatherMetrics.find(m => m.value === metricValue) || weatherMetrics[0];
};

// Chart colors
const CHART_STOCK_COLOR = "#FACC15";
const CHART_STOCK_WIDTH = 4;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#18181B] border border-[#27272A] p-3 font-data text-xs">
        <p className="text-[#A1A1AA] mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-bold">{entry.value?.toFixed(1)}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function PatternMatch() {
  const [cities, setCities] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  // Core metrics only by default - temperature, precipitation, wind (3 most impactful, fast to scan)
  const DEFAULT_METRICS = ["temperature_mean", "precipitation_sum", "wind_speed_max"];
  const [selectedMetrics, setSelectedMetrics] = useState(DEFAULT_METRICS);
  const [timeRange, setTimeRange] = useState("90");
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overlayModal, setOverlayModal] = useState({ open: false, stock: null });
  const [predictionModal, setPredictionModal] = useState({ open: false, stock: null });
  const [crossSectorData, setCrossSectorData] = useState([]);
  const [crossSectorLoading, setCrossSectorLoading] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [stockCodeInput, setStockCodeInput] = useState("");
  const [searchMode, setSearchMode] = useState("sector");
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const { isPremium } = useAuth(); // "sector" | "stock"

  // Get city + sector from URL query params (passed from Dashboard)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cityParam = params.get('city');
    const sectorParam = params.get('sector');
    if (cityParam) setSelectedCity(cityParam);
    if (sectorParam) setSelectedSector(sectorParam);
    // Keep 90 days default - 365 days times out on large sectors
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [citiesRes, sectorsRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/sectors`)
      ]);
      setCities(citiesRes.data);
      setSectors(sectorsRes.data);
      
      // Only set default city if not already set from URL
      const params = new URLSearchParams(window.location.search);
      const cityParam = params.get('city');
      if (!cityParam && citiesRes.data.length > 0) {
        setSelectedCity(citiesRes.data[0].key);
      }
      if (sectorsRes.data.length > 0) {
        // Only set default sector if not already set from URL
        const params = new URLSearchParams(window.location.search);
        const sectorParam = params.get('sector');
        if (!sectorParam) {
          const defaultSector = sectorsRes.data.find(s => s.key === "utilities") || sectorsRes.data[0];
          setSelectedSector(defaultSector.key);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const fetchPatternMatch = useCallback(async () => {
    if (searchMode === "stock") {
      fetchSingleStock();
      return;
    }
    if (!selectedCity || !selectedSector || selectedMetrics.length === 0) return;
    
    setLoading(true);
    setMatchData(null);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      
      const response = await axios.get(`${API}/pattern-match-multi/${selectedCity}`, {
        params: {
          sector: selectedSector,
          weather_metrics: selectedMetrics.join(','),
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        },
        timeout: 300000 // 5 minute timeout for large scans
      });
      
      setMatchData(response.data);

      // Fetch best match from each OTHER sector for cross-sector section
      fetchCrossSectorBest();
    } catch (error) {
      console.error("Failed to fetch pattern match:");
      setMatchData({ error: true, message: error?.response?.data?.detail || "Request timed out or failed. Try a smaller sector or shorter time range." });
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedSector, selectedMetrics, timeRange]);

  const fetchCrossSectorBest = async () => {
    if (!selectedCity || !selectedSector) return;
    setCrossSectorLoading(true);
    try {
      const sectorsRes = await axios.get(`${API}/sectors`);
      const otherSectors = sectorsRes.data.filter(s => s.key !== selectedSector && s.key !== "all");
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));

      const results = await Promise.allSettled(
        otherSectors.map(sector =>
          axios.get(`${API}/pattern-match-multi/${selectedCity}`, {
            params: {
              sector: sector.key,
              weather_metrics: selectedMetrics.slice(0, 3).join(','),
              start_date: startDate.toISOString().split('T')[0],
              end_date: endDate.toISOString().split('T')[0]
            },
            timeout: 60000
          }).then(res => ({ sector: sector.name, sectorKey: sector.key, stock: res.data.stocks?.[0] || null }))
        )
      );
      const best = results
        .filter(r => r.status === "fulfilled" && r.value?.stock)
        .map(r => r.value);
      setCrossSectorData(best);
    } catch (err) {
      console.error("Cross-sector fetch failed:", err);
    } finally {
      setCrossSectorLoading(false);
    }
  };

  const fetchSingleStock = async () => {
    const raw = stockCodeInput.trim().toUpperCase();
    if (!raw || !selectedCity) return;
    const symbol = raw.endsWith(".AX") ? raw : `${raw}.AX`;
    setLoading(true);
    setMatchData(null);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      const res = await axios.get(`${API}/overlay-data-multi/${selectedCity}/${symbol}`, {
        params: {
          weather_metrics: selectedMetrics.join(","),
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
        },
        timeout: 60000,
      });
      // Shape into the same structure as pattern-match results
      const d = res.data;
      const topMetric = selectedMetrics[0] || "temperature_mean";
      const corr = d.correlations?.[topMetric]?.correlation || 0;
      const fakeStock = {
        symbol,
        name: d.stock_name || symbol.replace(".AX", ""),
        avg_match_score: Math.round(Math.abs(corr) * 100),
        top_3_metrics: selectedMetrics.slice(0, 3).map(m => ({ metric: m, correlation: corr })),
        correlation_by_metric: d.correlations || {},
        sector: "—",
      };
      setMatchData({ stocks: [fakeStock], city: cities.find(c => c.key === selectedCity)?.name });
    } catch (err) {
      setMatchData({ error: true, message: `Could not find data for "${raw}". Check the ASX ticker and try again.` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchMode === "sector") fetchPatternMatch();
  }, [fetchPatternMatch, searchMode]);

  const handleViewOverlay = (stock) => {
    setOverlayModal({ open: true, stock });
  };

  const handlePrediction = (e, stock) => {
    e.stopPropagation();
    setPredictionModal({ open: true, stock });
  };

  const toggleMetric = (metricValue) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricValue)) {
        return prev.filter(m => m !== metricValue);
      } else {
        return [...prev, metricValue];
      }
    });
  };

  const selectAllMetrics = () => {
    setSelectedMetrics(weatherMetrics.map(m => m.value));
  };

  const clearAllMetrics = () => {
    setSelectedMetrics([]);
  };

  const getExportParams = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(timeRange));
    return new URLSearchParams({
      city: selectedCity,
      sector: selectedSector,
      weather_metrics: selectedMetrics.join(','),
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    }).toString();
  };

  const handleExportCSV = () => {
    window.open(`${API}/export-csv?${getExportParams()}`, '_blank');
  };

  const handleExportPDF = () => {
    window.open(`${API}/export-pdf?${getExportParams()}`, '_blank');
  };

  return (
    <div className="p-8" data-testid="pattern-match-page">
      {/* Header */}
      <header className="mb-8">
        <h1 className="font-heading text-4xl font-black text-white mb-2">
          Pattern Matching
        </h1>
        <p className="text-[#A1A1AA]">
          Find stocks whose movements most closely match weather patterns
        </p>
      </header>

      {/* Controls */}
      <div className="bento-card mb-8" data-testid="pattern-controls">

        {/* Mode toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSearchMode("sector")}
            className={cn("px-4 py-2 text-sm font-medium rounded transition-colors",
              searchMode === "sector" ? "bg-[#38BDF8] text-black" : "bg-[#18181B] text-[#A1A1AA] hover:text-white border border-[#27272A]"
            )}
          >Browse Sector</button>
          <button
            onClick={() => setSearchMode("stock")}
            className={cn("px-4 py-2 text-sm font-medium rounded transition-colors",
              searchMode === "stock" ? "bg-[#22c55e] text-black" : "bg-[#18181B] text-[#A1A1AA] hover:text-white border border-[#27272A]"
            )}
          >Search by Stock Code</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* City — always shown */}
          <div>
            <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">City</label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A]" data-testid="city-select">
                <MapPin className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select City" />
              </SelectTrigger>
              <SelectContent>
                {cities.map(city => (
                  <SelectItem key={city.key} value={city.key}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sector OR Stock Code */}
          {searchMode === "sector" ? (
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">Sector</label>
              <Select value={selectedSector} onValueChange={setSelectedSector}>
                <SelectTrigger className="bg-[#09090B] border-[#27272A]" data-testid="sector-select">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Select Sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map(sector => (
                    <SelectItem key={sector.key} value={sector.key}>
                      {sector.name} ({sector.stock_count} stocks)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">ASX Stock Code</label>
              <div className="relative">
                <input
                  type="text"
                  value={stockCodeInput}
                  onChange={e => setStockCodeInput(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === "Enter" && fetchSingleStock()}
                  placeholder="e.g. BHP, CBA, WBC"
                  className="w-full bg-[#09090B] border border-[#27272A] text-white text-sm px-3 py-2 rounded-md placeholder-[#52525B] focus:outline-none focus:border-[#22c55e]"
                  data-testid="stock-code-input"
                />
              </div>
              <p className="text-[10px] text-[#52525B] mt-1">Enter ASX ticker without .AX suffix</p>
            </div>
          )}

          {/* Time Range */}
          <div>
            <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">Time Range</label>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A]" data-testid="time-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map(range => (
                  <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              onClick={searchMode === "stock" ? fetchSingleStock : fetchPatternMatch}
              disabled={loading || selectedMetrics.length === 0 || (searchMode === "stock" && !stockCodeInput.trim())}
              className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-black font-medium"
              data-testid="find-matches-button"
            >
              {loading
                ? (searchMode === "stock" ? "Searching..." : `Scanning ${sectors.find(s => s.key === selectedSector)?.stock_count || ""}...`)
                : (searchMode === "stock" ? `Analyse ${stockCodeInput || "Stock"}` : "Find Matches")
              }
            </Button>
          </div>
        </div>

        {/* Premium Deep Pattern Matching toggle */}
        <div className="mt-3 pt-3 border-t border-[#27272A] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { if (!isPremium) setPremiumModalOpen(true); }}
              className={cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-all",
                isPremium
                  ? "bg-[#22c55e]/10 border-[#22c55e]/40 text-[#22c55e] cursor-default"
                  : "bg-[#18181B] border-[#27272A] text-[#A1A1AA] hover:border-[#22c55e]/40 hover:text-[#22c55e] cursor-pointer"
              )}
            >
              <span className="text-base">⚡</span>
              Premium Deep Pattern-matching
              {!isPremium && <span className="ml-1 text-[10px] bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/30 px-1.5 py-0.5 rounded">Upgrade</span>}
              {isPremium && <span className="ml-1 text-[10px] text-[#22c55e]">Active</span>}
            </button>
          </div>
          <p className="text-xs text-[#52525B]">Historical analogue search · 2yr data</p>
        </div>

        {/* Weather Metrics Multi-Select */}
        <div>
          <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">
            Weather Metrics ({selectedMetrics.length} of {weatherMetrics.length} selected)
          </label>
          <Popover open={metricsOpen} onOpenChange={setMetricsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between bg-[#09090B] border-[#27272A] text-left font-normal"
                data-testid="metrics-dropdown"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#A1A1AA]" />
                  <span className="text-[#A1A1AA]">
                    {selectedMetrics.length === 0 
                      ? "Select metrics..." 
                      : selectedMetrics.length === weatherMetrics.length 
                        ? "All metrics selected"
                        : `${selectedMetrics.length} metrics selected`}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-[#18181B] border-[#27272A]" align="start">
              {/* Quick actions */}
              <div className="flex justify-between p-3 border-b border-[#27272A]">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={selectAllMetrics}
                  className="text-xs text-[#38BDF8] hover:text-[#38BDF8] hover:bg-[#27272A]"
                >
                  Select All
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearAllMetrics}
                  className="text-xs text-[#F43F5E] hover:text-[#F43F5E] hover:bg-[#27272A]"
                >
                  Clear All
                </Button>
              </div>
              
              {/* Metrics list */}
              <div className="max-h-64 overflow-y-auto p-2">
                {weatherMetrics.map(metric => {
                  const Icon = metric.icon;
                  const isSelected = selectedMetrics.includes(metric.value);
                  return (
                    <div
                      key={metric.value}
                      className={cn(
                        "flex items-center gap-3 p-2 cursor-pointer hover:bg-[#27272A] rounded",
                        isSelected && "bg-[#27272A]/50"
                      )}
                      onClick={() => toggleMetric(metric.value)}
                      data-testid={`metric-checkbox-${metric.value}`}
                    >
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleMetric(metric.value)}
                        className="border-[#52525B] data-[state=checked]:bg-[#38BDF8] data-[state=checked]:border-[#38BDF8]"
                      />
                      <Icon className="w-4 h-4" style={{ color: metric.color }} />
                      <span className="text-sm text-white flex-1">{metric.label}</span>
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: metric.color }}
                      />
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Selected metrics tags */}
          {selectedMetrics.length > 0 && selectedMetrics.length < weatherMetrics.length && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedMetrics.map(metricValue => {
                const metric = getMetricInfo(metricValue);
                return (
                  <span
                    key={metricValue}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#27272A] border border-[#3f3f46] text-white"
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: metric.color }} />
                    {metric.label}
                    <X 
                      className="w-3 h-3 cursor-pointer hover:text-[#F43F5E]" 
                      onClick={() => toggleMetric(metricValue)}
                    />
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20" data-testid="loading-indicator">
          <div className="relative w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-[#27272A]" />
            <div className="absolute inset-0 rounded-full border-4 border-t-[#38BDF8] border-r-transparent border-b-transparent border-l-transparent animate-spin" />
            <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-[#FACC15] border-b-transparent border-l-transparent animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
          </div>
          <p className="text-white font-heading text-lg mb-2">
            Scanning stocks in {sectors.find(s => s.key === selectedSector)?.name || 'sector'}
          </p>
          <p className="text-[#A1A1AA] text-sm mb-4">
            Analyzing {selectedMetrics.length} weather metrics &mdash; this may take 15-30 seconds
          </p>
          <div className="w-64 h-1.5 bg-[#27272A] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#38BDF8] to-[#FACC15] rounded-full animate-pulse" style={{ width: '65%', animation: 'loading-progress 3s ease-in-out infinite' }} />
          </div>
          <p className="text-[#52525B] text-xs mt-3">
            Finding top 5 matches by category-based correlation...
          </p>
          <style>{`
            @keyframes loading-progress {
              0% { width: 10%; }
              50% { width: 85%; }
              100% { width: 10%; }
            }
          `}</style>
        </div>
      )}

      {!loading && matchData?.error && (
        <div className="bento-card border-[#F43F5E] border text-center py-12" data-testid="error-message">
          <p className="text-[#F43F5E] font-heading text-lg mb-2">Scan Failed</p>
          <p className="text-[#A1A1AA] text-sm">{matchData.message}</p>
          <Button 
            onClick={fetchPatternMatch}
            className="mt-4 bg-[#38BDF8] hover:bg-[#0EA5E9] text-black font-medium"
          >
            Retry
          </Button>
        </div>
      )}

      {!loading && matchData && !matchData.error && (
        <>
          {/* Results Summary */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl font-bold text-white">
                Top 5 Pattern Matches
              </h2>
              <p className="text-sm text-[#52525B]">
                {matchData.city} &bull; {matchData.sector} &bull; Ranked by top 3 metric average
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="bg-[#18181B] border-[#27272A] hover:bg-[#27272A] text-[#A1A1AA] hover:text-white"
                  data-testid="export-csv-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPDF}
                  className="bg-[#18181B] border-[#27272A] hover:bg-[#27272A] text-[#A1A1AA] hover:text-white"
                  data-testid="export-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  PDF
                </Button>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#A1A1AA]">
                  Scanned <span className="text-[#38BDF8] font-data">{matchData.total_scanned || '?'}</span> stocks &bull; <span className="text-[#10B981] font-data">{matchData.total_analyzed || '?'}</span> analyzed
                </p>
                <p className="text-xs text-[#52525B]">Showing top {matchData.stocks?.length || 0} of {matchData.total_analyzed || '?'} &bull; {selectedMetrics.length} metrics</p>
              </div>
            </div>
          </div>

          {/* Match Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="match-cards">
            {matchData.stocks?.map((stock, index) => (
              <motion.div
                key={stock.symbol}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  "bento-card",
                  index === 0 && "border-[#10B981] border-2"
                )}
                data-testid={`match-card-${stock.symbol}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-data text-xl font-bold text-white">
                      {stock.symbol.replace('.AX', '')}
                    </p>
                    <p className="text-xs text-[#52525B] truncate max-w-[200px]">
                      {stock.name}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={cn(
                      "px-2 py-1 text-xs font-data mb-1",
                      index === 0 ? "bg-[#10B981]/20 text-[#10B981] border border-[#10B981]" : "bg-[#27272A] text-[#A1A1AA]"
                    )}>
                      {index === 0 ? "BEST MATCH" : `#${index + 1}`}
                    </div>
                    <p className="text-xs text-[#52525B]">${stock.latest_price}</p>
                    <p className={cn(
                      "text-xs font-data",
                      stock.price_change_percent >= 0 ? "text-[#10B981]" : "text-[#F43F5E]"
                    )}>
                      {stock.price_change_percent >= 0 ? '+' : ''}{stock.price_change_percent}%
                    </p>
                  </div>
                </div>

                {/* Average Score - Based on Top 3 Metrics */}
                <div className="mb-4 p-3 bg-[#09090B] border border-[#27272A]">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs text-[#A1A1AA]">Best Match Score</span>
                      <p className="text-[10px] text-[#52525B]">Average of top 3 metrics</p>
                    </div>
                    <span className="font-data text-2xl font-bold text-white">
                      {stock.avg_match_score}%
                    </span>
                  </div>
                  <div className="h-2 bg-[#27272A] overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#38BDF8] to-[#10B981]"
                      style={{ width: `${stock.avg_match_score}%` }}
                    />
                  </div>
                  
                  {/* Top 3 Metrics Display */}
                  {stock.top_3_metrics && stock.top_3_metrics.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#27272A]">
                      <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-2">Top 3 Category Matches</p>
                      <div className="space-y-2">
                        {stock.top_3_metrics.slice(0, 3).map((m, idx) => {
                          const info = getMetricInfo(m.metric);
                          const Icon = info.icon;
                          return (
                            <div key={m.metric} className="flex items-center gap-2">
                              <span className="text-xs text-[#52525B] w-4">#{idx + 1}</span>
                              <Icon className="w-3 h-3" style={{ color: info.color }} />
                              <span className="text-xs text-[#A1A1AA]">{m.category_name}:</span>
                              <span className="text-xs text-white flex-1">{info.label}</span>
                              <span className="font-data text-xs font-bold text-white">{m.match_score}%</span>
                              <span className={cn(
                                "font-data text-xs",
                                m.correlation > 0 ? "text-[#10B981]" : "text-[#F43F5E]"
                              )}>
                                {m.correlation > 0 ? '+' : ''}{m.correlation}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {stock.has_strong_triple && (
                        <div className="mt-2 px-2 py-1 bg-[#10B981]/10 border border-[#10B981]/30 text-[10px] text-[#10B981] text-center">
                          Strong Triple Match ✓
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Metrics Breakdown */}
                <div className="mb-4">
                  <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2">Correlation by Metric</p>
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={(stock.all_metrics || []).filter(m => m.match_score > 0).map(m => ({
                          name: getMetricInfo(m.metric).label.split(' ')[0],
                          score: m.match_score,
                          correlation: m.correlation,
                          color: getMetricInfo(m.metric).color
                        }))}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: '#A1A1AA', fontSize: 9 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: '#A1A1AA', fontSize: 9 }} width={70} />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-[#18181B] border border-[#27272A] p-2 text-xs">
                                  <p className="text-white font-bold">{data.name}</p>
                                  <p className="text-[#A1A1AA]">Score: {data.score}%</p>
                                  <p className={data.correlation > 0 ? "text-[#10B981]" : "text-[#F43F5E]"}>
                                    Corr: {data.correlation > 0 ? '+' : ''}{data.correlation}
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                          {(stock.all_metrics || []).filter(m => m.match_score > 0).map((m, i) => (
                            <Cell key={i} fill={getMetricInfo(m.metric).color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Individual Metrics Grid */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {(stock.all_metrics || []).slice(0, 6).map(m => {
                    const metricInfo = getMetricInfo(m.metric);
                    const Icon = metricInfo.icon;
                    return (
                      <div 
                        key={m.metric} 
                        className="p-2 bg-[#09090B] border border-[#27272A] text-center"
                        title={`${metricInfo.label}: ${m.correlation} correlation`}
                      >
                        <Icon className="w-3 h-3 mx-auto mb-1" style={{ color: metricInfo.color }} />
                        <p className={cn(
                          "font-data text-xs font-bold",
                          m.correlation > 0 ? "text-[#10B981]" : m.correlation < 0 ? "text-[#F43F5E]" : "text-[#52525B]"
                        )}>
                          {m.correlation !== 0 ? (m.correlation > 0 ? '+' : '') + m.correlation : 'N/A'}
                        </p>
                        <p className="text-[8px] text-[#52525B] uppercase">{m.strength}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 bg-transparent border-[#27272A] hover:bg-[#27272A] text-[#A1A1AA] hover:text-white"
                    onClick={() => handleViewOverlay({ ...stock, symbol: stock.symbol, all_metrics: stock.all_metrics })}
                    data-testid={`overlay-btn-${stock.symbol}`}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Overlay
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-black font-medium"
                    onClick={(e) => handlePrediction(e, { ...stock, symbol: stock.symbol })}
                    data-testid={`predict-btn-${stock.symbol}`}
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Predict
                  </Button>
                  {!isPremium && (
                    <Button
                      size="sm"
                      className="bg-[#22c55e]/10 hover:bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 font-medium"
                      onClick={(e) => { e.stopPropagation(); setPremiumModalOpen(true); }}
                    >
                      ⚡ Upgrade
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Legend */}
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            {selectedMetrics.slice(0, 6).map(metricValue => {
              const metric = getMetricInfo(metricValue);
              return (
                <div key={metricValue} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: metric.color }} />
                  <span className="text-xs text-[#A1A1AA]">{metric.label}</span>
                </div>
              );
            })}
            {selectedMetrics.length > 6 && (
              <span className="text-xs text-[#52525B]">+{selectedMetrics.length - 6} more</span>
            )}
          </div>

          {/* Cross-Sector Best Matches */}
          <div className="mt-10">
            <h2 className="font-heading text-xl font-bold text-white mb-1">Best Match From Other Sectors</h2>
            <p className="text-sm text-[#52525B] mb-6">Top performing stock from each sector for the same city &amp; weather patterns</p>
            {crossSectorLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-24 bg-[#18181B] skeleton-pulse rounded" />
                ))}
              </div>
            ) : crossSectorData.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {crossSectorData.map(({ sector, sectorKey, stock }) => (
                  <motion.div
                    key={sectorKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bento-card flex items-center justify-between gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-0.5">{sector}</p>
                      <p className="font-data text-base font-bold text-white">{stock.symbol.replace('.AX', '')}</p>
                      <p className="text-xs text-[#A1A1AA] truncate">{stock.name}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-data text-lg font-bold text-[#38BDF8]">{stock.avg_match_score}%</p>
                      <p className="text-[10px] text-[#52525B]">match score</p>
                      <button
                        onClick={() => setPredictionModal({ open: true, stock })}
                        className="mt-1 text-[10px] text-[#22c55e] hover:text-white transition-colors"
                      >
                        Predict →
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : null}
          </div>
        </>
      )}

      {!loading && matchData && !matchData.error && matchData?.stocks?.length === 0 && (
        <div className="text-center py-16">
          <LineChartIcon className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
          <p className="text-[#A1A1AA] mb-2">No pattern matches found</p>
          <p className="text-sm text-[#52525B]">
            Try selecting a different city, sector, or weather metrics
          </p>
        </div>
      )}

      {/* Overlay Modal */}
      <OverlayModal 
        isOpen={overlayModal.open}
        onClose={() => setOverlayModal({ open: false, stock: null })}
        stock={overlayModal.stock}
        city={selectedCity}
        cityName={matchData?.city}
        selectedMetrics={selectedMetrics}
        timeRange={timeRange}
      />

      {/* Prediction Modal */}
      <PredictionModal 
        isOpen={predictionModal.open}
        onClose={() => setPredictionModal({ open: false, stock: null })}
        stock={predictionModal.stock}
        city={selectedCity}
        cityName={matchData?.city}
        selectedMetrics={selectedMetrics}
        timeRange={timeRange}
        onUpgrade={() => { setPredictionModal({ open: false, stock: null }); setPremiumModalOpen(true); }}
      />

      {/* Premium Upgrade Modal */}
      <PremiumModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        featureName="Premium Deep Pattern-matching"
      />
    </div>
  );
}

function OverlayModal({ isOpen, onClose, stock, city, cityName, selectedMetrics, timeRange }) {
  const [overlayData, setOverlayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleMetrics, setVisibleMetrics] = useState({});

  useEffect(() => {
    if (isOpen && stock) {
      fetchOverlayData();
      // Default: show top 3 category metrics + stock
      const defaults = {};
      weatherMetrics.forEach(m => { defaults[m.value] = false; });
      (stock.top_3_metrics || []).forEach(m => { defaults[m.metric] = true; });
      setVisibleMetrics(defaults);
    }
  }, [isOpen, stock]);

  const fetchOverlayData = async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      const response = await axios.get(`${API}/overlay-data-multi/${city}/${stock.symbol}`, {
        params: {
          weather_metrics: weatherMetrics.map(m => m.value).join(','),
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      setOverlayData(response.data);
    } catch (error) {
      console.error("Failed to fetch overlay data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMetric = (metric) => {
    setVisibleMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
  };

  const toggleCategory = (catKey) => {
    const catMetrics = metricCategories[catKey].metrics;
    const allOn = catMetrics.every(m => visibleMetrics[m]);
    const newState = { ...visibleMetrics };
    catMetrics.forEach(m => { newState[m] = !allOn; });
    setVisibleMetrics(newState);
  };

  const selectAll = () => {
    const newState = {};
    weatherMetrics.forEach(m => { newState[m.value] = true; });
    setVisibleMetrics(newState);
  };

  const selectNone = () => {
    const newState = {};
    weatherMetrics.forEach(m => { newState[m.value] = false; });
    setVisibleMetrics(newState);
  };

  if (!stock) return null;

  const activeCount = Object.values(visibleMetrics).filter(Boolean).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl bg-[#09090B] border-[#27272A] max-h-[90vh] overflow-y-auto" data-testid="overlay-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white flex items-center gap-3">
            <span>{stock.symbol.replace('.AX', '')}</span>
            <span className="text-[#52525B]">×</span>
            <span className="text-[#38BDF8]">Weather Overlay</span>
          </DialogTitle>
        </DialogHeader>

        {/* Metric Toggle Panel */}
        <div className="p-3 bg-[#18181B] border border-[#27272A] rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Toggle Weather Metrics ({activeCount} shown)</p>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-xs text-[#38BDF8] hover:underline" data-testid="overlay-select-all">All</button>
              <span className="text-xs text-[#52525B]">|</span>
              <button onClick={selectNone} className="text-xs text-[#A1A1AA] hover:underline" data-testid="overlay-select-none">None</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(metricCategories).map(([catKey, cat]) => (
              <div key={catKey} className="space-y-1">
                <button
                  onClick={() => toggleCategory(catKey)}
                  className="text-[10px] text-[#52525B] uppercase tracking-wider hover:text-[#A1A1AA] w-full text-left"
                >
                  {cat.name}
                </button>
                {cat.metrics.map(metricVal => {
                  const info = getMetricInfo(metricVal);
                  const Icon = info.icon;
                  const isOn = visibleMetrics[metricVal];
                  return (
                    <label
                      key={metricVal}
                      className={cn(
                        "flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors text-xs",
                        isOn ? "bg-[#27272A] text-white" : "text-[#52525B] hover:text-[#A1A1AA]"
                      )}
                    >
                      <Checkbox
                        checked={isOn}
                        onCheckedChange={() => toggleMetric(metricVal)}
                        className="h-3.5 w-3.5 border-[#52525B]"
                        data-testid={`overlay-toggle-${metricVal}`}
                      />
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: info.color }} />
                      <Icon className="w-3 h-3 shrink-0" style={{ color: info.color }} />
                      <span className="truncate">{info.label}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#27272A]">
            <div className="w-4 h-1.5 bg-[#FACC15] rounded" />
            <span className="text-xs text-[#FACC15] font-medium">Stock Price (always shown)</span>
          </div>
        </div>

        {loading ? (
          <div className="h-96 bg-[#18181B] skeleton-pulse rounded-lg" />
        ) : overlayData ? (
          <div className="space-y-4">
            {/* Main Overlay Chart */}
            <div className="bento-card">
              <h3 className="font-heading text-lg font-bold text-white mb-4">
                Pattern Overlay (Normalized 0-100)
              </h3>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={overlayData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 10 }} tickFormatter={(val) => val.slice(5)} />
                    <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} domain={[0, 100]} label={{ value: 'Normalized %', angle: -90, position: 'insideLeft', fill: '#52525B', fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={50} stroke="#27272A" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="stock_normalized" name="Stock Price" stroke={CHART_STOCK_COLOR} strokeWidth={CHART_STOCK_WIDTH} dot={false} />
                    {weatherMetrics.map(m => (
                      visibleMetrics[m.value] && (
                        <Line
                          key={m.value}
                          type="monotone"
                          dataKey={`${m.value}_normalized`}
                          name={m.label}
                          stroke={m.color}
                          strokeWidth={2}
                          dot={false}
                        />
                      )
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Stock Range Info */}
            <div className="bento-card">
              <p className="text-xs text-[#A1A1AA] uppercase mb-2">Stock Price Range</p>
              <p className="font-data text-lg font-bold text-white">
                ${overlayData.stock_range?.min} - ${overlayData.stock_range?.max} AUD
              </p>
            </div>

            <div className="text-center text-xs text-[#52525B]">
              <p>All values normalized to 0-100 scale for visual comparison.</p>
            </div>
          </div>
        ) : (
          <div className="h-96 flex items-center justify-center">
            <p className="text-[#A1A1AA]">Failed to load overlay data</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PredictionModal({ isOpen, onClose, stock, city, cityName, selectedMetrics, timeRange, onUpgrade }) {
  const [predictionData, setPredictionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleMetrics, setVisibleMetrics] = useState({});
  const { isPremium } = useAuth();

  useEffect(() => {
    if (isOpen && stock) {
      fetchPrediction();
      // Default: show top 3 matched metrics
      const defaults = {};
      weatherMetrics.forEach(m => { defaults[m.value] = false; });
      (stock.top_3_metrics || []).forEach(m => { defaults[m.metric] = true; });
      // Fallback: show selected metrics if no top_3
      if (!(stock.top_3_metrics?.length)) {
        selectedMetrics.slice(0, 3).forEach(m => { defaults[m] = true; });
      }
      setVisibleMetrics(defaults);
    }
  }, [isOpen, stock]);

  const fetchPrediction = async () => {
    setLoading(true);
    setPredictionData(null);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      const dateParams = {
        weather_metrics: selectedMetrics.join(','),
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };

      // Try the full dual-prediction endpoint first
      try {
        const response = await axios.get(`${API}/dual-prediction-multi/${city}/${stock.symbol}`, {
          params: dateParams,
          timeout: 60000
        });
        // If partial response (thin stock), augment with overlay data
        if (response.data._partial) {
          let chartData = null;
          let currentPrice = null;
          try {
            const overlayRes = await axios.get(`${API}/overlay-data-multi/${city}/${stock.symbol}`, {
              params: { weather_metrics: selectedMetrics.join(','), start_date: dateParams.start_date, end_date: dateParams.end_date },
              timeout: 30000
            });
            chartData = (overlayRes.data.data || []).map(row => ({ ...row, type: 'historical' }));
            currentPrice = overlayRes.data.data?.at(-1)?.stock_raw || null;
          } catch (_) {}
          setPredictionData({
            ...response.data,
            _source: 'partial',
            chart_data: chartData,
            stock_prediction: { ...response.data.stock_prediction, current: currentPrice }
          });
        } else {
          setPredictionData({ ...response.data, _source: 'full' });
        }
        return;
      } catch (fullErr) {
        console.warn("dual-prediction-multi failed, trying fallback:", fullErr?.response?.data?.detail);
      }

      // Fallback: use the simpler prediction-signal endpoint
      const fallback = await axios.get(`${API}/prediction-signal/${city}/${stock.symbol}`, {
        params: {
          start_date: dateParams.start_date,
          end_date: dateParams.end_date
        },
        timeout: 30000
      });

      // Also fetch overlay data for chart and current price
      let chartData = null;
      let currentPrice = null;
      try {
        const overlayRes = await axios.get(`${API}/overlay-data-multi/${city}/${stock.symbol}`, {
          params: {
            weather_metrics: selectedMetrics.join(','),
            start_date: dateParams.start_date,
            end_date: dateParams.end_date
          },
          timeout: 30000
        });
        // Shape overlay data to match chart_data format (historical only, no forecast)
        chartData = (overlayRes.data.data || []).map(row => ({ ...row, type: 'historical' }));
        // Get current price from last entry
        const lastEntry = overlayRes.data.data?.at(-1);
        currentPrice = lastEntry?.stock_raw || null;
      } catch (overlayErr) {
        console.warn("Overlay fetch also failed, showing signal only:", overlayErr?.message);
      }

      // Shape the fallback response to match what the modal expects
      setPredictionData({
        _source: 'fallback',
        investment_signal: {
          signal: fallback.data.signal,
          reason: fallback.data.rationale,
          confidence: fallback.data.confidence,
        },
        stock_prediction: {
          current: currentPrice,
          projected_7d: null,
          change_percent: null,
          trend: fallback.data.temperature_trend,
        },
        chart_data: chartData,
        correlations: { temperature_mean: fallback.data.correlation },
      });
    } catch (error) {
      console.error("All prediction endpoints failed:", error);
      const detail = error.response?.data?.detail;
      setPredictionData({ error: detail || "Failed to load predictive data. Try a longer time range or different stock." });
    } finally {
      setLoading(false);
    }
  };

  const toggleMetric = (metric) => {
    setVisibleMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
  };

  const signal = predictionData?.investment_signal?.signal;
  const signalStyles = {
    bullish: { border: 'border-[#10B981]', bg: 'bg-[#10B981]/5', text: 'text-[#10B981]', icon: <TrendingUp className="w-8 h-8 text-[#10B981]" /> },
    bearish: { border: 'border-[#F43F5E]', bg: 'bg-[#F43F5E]/5', text: 'text-[#F43F5E]', icon: <TrendingDown className="w-8 h-8 text-[#F43F5E]" /> },
    neutral: { border: 'border-[#6366F1]', bg: 'bg-[#6366F1]/5', text: 'text-[#6366F1]', icon: <Minus className="w-8 h-8 text-[#6366F1]" /> },
  };
  const ss = signalStyles[signal] || signalStyles.neutral;

  const activeMetrics = weatherMetrics.filter(m => visibleMetrics[m.value]);
  const ticker = stock?.symbol?.replace('.AX', '') || '';

  // Split chart data into historical and forecast
  const historicalData = predictionData?.chart_data?.filter(d => d.type === 'historical') || [];
  const forecastData = predictionData?.chart_data?.filter(d => d.type === 'forecast') || [];
  const allChartData = predictionData?.chart_data || [];
  const forecastStartDate = forecastData[0]?.date;

  if (!stock) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-[#09090B] border-[#27272A] max-h-[90vh] overflow-y-auto" data-testid="prediction-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white flex items-center gap-3">
            <Zap className="w-6 h-6 text-[#F59E0B]" />
            <span>{ticker} — Weather Correlation & 30-Day Forecast</span>
          </DialogTitle>
          <p className="text-sm text-[#52525B]">{cityName} · {timeRange} days historical · 30 days projected</p>
        </DialogHeader>

        {/* Premium upgrade banner — shown to free users */}
        {!isPremium && (
          <div className="flex items-center justify-between bg-[#22c55e]/5 border border-[#22c55e]/20 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-lg">⚡</span>
              <div>
                <p className="text-sm font-medium text-white">Unlock β-Regression & Deep Pattern Matching</p>
                <p className="text-xs text-[#52525B]">Premium gives you weather-driven price projections and 2-year historical analogue search</p>
              </div>
            </div>
            <button
              onClick={() => { onClose(); onUpgrade?.(); }}
              className="shrink-0 ml-4 px-4 py-2 bg-[#22c55e] hover:bg-[#16a34a] text-black text-sm font-bold rounded-lg transition-colors"
            >
              Upgrade $12.34/mo
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-4 py-8">
            <div className="h-16 bg-[#18181B] skeleton-pulse rounded-lg" />
            <div className="h-80 bg-[#18181B] skeleton-pulse rounded-lg" />
          </div>
        ) : predictionData?.error ? (
          <div className="h-48 flex flex-col items-center justify-center gap-3">
            <Zap className="w-10 h-10 text-[#52525B]" />
            <p className="text-[#A1A1AA] text-center max-w-md">{predictionData.error}</p>
            <p className="text-xs text-[#52525B]">Try selecting a longer time range or a different stock.</p>
          </div>
        ) : predictionData ? (
          <div className="space-y-5">

            {/* Signal Banner */}
            <div className={cn("p-4 border-2 rounded-lg flex items-center gap-5", ss.border, ss.bg)}>
              {ss.icon}
              <div className="flex-1">
                <p className={cn("font-heading text-2xl font-black uppercase", ss.text)}>{signal}</p>
                <p className="text-sm text-[#A1A1AA]">{predictionData.investment_signal?.reason}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#52525B]">Confidence</p>
                <p className="font-data text-2xl font-bold text-white">{predictionData.investment_signal?.confidence}%</p>
                <div className="w-24 h-1.5 bg-[#27272A] rounded overflow-hidden mt-1">
                  <div className={cn("h-full", ss.text.replace('text-', 'bg-'))}
                    style={{ width: `${predictionData.investment_signal?.confidence || 0}%`, backgroundColor: signal === 'bullish' ? '#10B981' : signal === 'bearish' ? '#F43F5E' : '#6366F1' }} />
                </div>
              </div>
            </div>

            {/* Main Chart — overlay style with historical + 30-day forecast */}
            <div className="bento-card">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading text-base font-bold text-white">Stock Price + Weather Correlation</h3>
                {predictionData._source === 'fallback' ? (
                  <span className="text-[10px] text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-0.5 rounded">Simplified — limited data</span>
                ) : (
                  <div className="flex items-center gap-4 text-xs text-[#52525B]">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-6 h-0.5 bg-[#FACC15]" />Actual</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-6 border-dashed border-t-2 border-[#22c55e]" />Projected</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#52525B] mb-4">Normalized 0–100 · stock price + weather · dashed = 30-day forecast</p>

              {predictionData.chart_data ? (
                <>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={allChartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                        <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 9 }} tickFormatter={val => val?.slice(5)} interval="preserveStartEnd" />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 9 }} domain={[0, 100]} />
                        <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px', fontSize: 11 }}
                          labelStyle={{ color: '#A1A1AA' }} formatter={(value, name) => [value?.toFixed(1), name]} />
                        {forecastStartDate && (
                          <ReferenceLine x={forecastStartDate} stroke="#F59E0B" strokeDasharray="4 3" strokeWidth={2}
                            label={{ value: '▶ Forecast', fill: '#F59E0B', fontSize: 9, position: 'insideTopRight' }} />
                        )}
                        <Line type="monotone" dataKey="stock_normalized" name={`${ticker} Price`}
                          stroke="#FACC15" strokeWidth={3} dot={false} connectNulls />
                        {/* β-Regression forecast line (premium feature indicator) */}
                        <Line type="monotone" dataKey="beta_normalized" name={`${ticker} β-Forecast`}
                          stroke="#22c55e" strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls />
                        {activeMetrics.map(metric => (
                          <Line key={metric.value} type="monotone" dataKey={`${metric.value}_normalized`}
                            name={metric.label} stroke={metric.color} strokeWidth={1.5} dot={false} connectNulls />
                        ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#27272A]">
                    <p className="text-xs text-[#52525B] mb-2 uppercase tracking-wider">Toggle weather overlay</p>
                    <div className="flex flex-wrap gap-2">
                      {weatherMetrics.map(m => (
                        <button key={m.value} onClick={() => toggleMetric(m.value)}
                          className={cn("flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors",
                            visibleMetrics[m.value] ? "border-current text-white bg-[#27272A]" : "border-[#27272A] text-[#52525B] hover:text-[#A1A1AA]"
                          )}
                          style={visibleMetrics[m.value] ? { borderColor: m.color, color: m.color } : {}}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-40 flex flex-col items-center justify-center text-center gap-2">
                  <p className="text-[#A1A1AA] text-sm">Chart unavailable — insufficient data for {ticker}</p>
                  <p className="text-[#52525B] text-xs">The prediction signal above is still valid. Try a longer time range for the full chart.</p>
                </div>
              )}
            </div>

            {/* Stock projection stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bento-card">
                <p className="text-xs text-[#52525B] mb-1">Current Price</p>
                <p className="font-data text-lg font-bold text-white">
                  {predictionData.stock_prediction?.current != null
                    ? `$${Number(predictionData.stock_prediction.current).toFixed(2)}`
                    : '—'}
                </p>
              </div>
              <div className="bento-card">
                <p className="text-xs text-[#52525B] mb-1">30-Day Projection</p>
                <p className="font-data text-lg font-bold text-white">
                  {predictionData.beta_prediction?.projected_price != null
                    ? `$${Number(predictionData.beta_prediction.projected_price).toFixed(2)}`
                    : predictionData.stock_prediction?.projected_7d != null
                    ? `$${Number(predictionData.stock_prediction.projected_7d).toFixed(2)}`
                    : '—'}
                </p>
                <p className="text-[10px] text-[#52525B] mt-0.5">
                  {predictionData.beta_prediction?.available ? "β-Regression" : "Ensemble model"}
                </p>
              </div>
              <div className="bento-card">
                <p className="text-xs text-[#52525B] mb-1">Projected Change</p>
                <p className={cn("font-data text-lg font-bold",
                  predictionData.stock_prediction?.change_percent != null
                    ? ((predictionData.stock_prediction.change_percent || 0) >= 0 ? 'text-[#10B981]' : 'text-[#F43F5E]')
                    : 'text-[#52525B]'
                )}>
                  {predictionData.stock_prediction?.change_percent != null
                    ? `${predictionData.stock_prediction.change_percent >= 0 ? '+' : ''}${Number(predictionData.stock_prediction.change_percent).toFixed(2)}%`
                    : '—'}
                </p>
              </div>
              <div className="bento-card">
                <p className="text-xs text-[#52525B] mb-1">Trend</p>
                <p className="font-data text-lg font-bold text-white capitalize">
                  {predictionData.stock_prediction?.trend || '—'}
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-center text-xs text-[#F59E0B] bg-[#F59E0B]/10 p-3 border border-[#F59E0B]/20 rounded-lg">
              <p>Experimental — for educational purposes only. Not financial advice. Always consult a licensed advisor before making investment decisions.</p>
            </div>

          </div>
        ) : (
          <div className="h-48 flex items-center justify-center">
            <p className="text-[#A1A1AA]">Failed to load prediction data</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
